/**
 * Interface for event emitter implementations
 * This abstraction allows for easy swapping of event emitter implementations
 * in the future without changing the tracking service
 *
 * The interface is designed to be compatible with Node's EventEmitter
 * to make it easier to swap implementations in the future without adding
 * additional dependencies at this time.
 */
export interface IEventEmitter<TEventMap extends Record<string, unknown> = Record<string, unknown>> {
  on<K extends keyof TEventMap>(eventName: K, callback: (data: TEventMap[K]) => void): void;
  off<K extends keyof TEventMap>(eventName: K, callback: (data: TEventMap[K]) => void): void;
  emit<K extends keyof TEventMap>(eventName: K, data: TEventMap[K]): void;
  removeAllListeners(): void;
}

type EventCallback<T> = (data: T) => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventMap<TEventMap> = Map<keyof TEventMap, Set<EventCallback<any>>>;

/**
 * Simple event emitter implementation using an array of callbacks
 * TODO: Consider using a third-party event emitter library like RxJS Subject or EventEmitter3
 * for more robust event handling, especially if the application grows in complexity
 */
export function createEventEmitter<
  TEventMap extends Record<string, unknown> = Record<string, unknown>
>(): IEventEmitter<TEventMap> {
  const listeners: EventMap<TEventMap> = new Map();

  function on<K extends keyof TEventMap>(eventName: K, callback: EventCallback<TEventMap[K]>): void {
    if (!listeners.has(eventName)) {
      listeners.set(eventName, new Set());
    }
    listeners.get(eventName)?.add(callback);
  }

  function off<K extends keyof TEventMap>(eventName: K, callback: EventCallback<TEventMap[K]>): void {
    const eventListeners = listeners.get(eventName);
    if (eventListeners) {
      eventListeners.delete(callback);
      if (eventListeners.size === 0) {
        listeners.delete(eventName);
      }
    }
  }

  function emit<K extends keyof TEventMap>(eventName: K, data: TEventMap[K]): void {
    const eventListeners = listeners.get(eventName);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${String(eventName)}:`, error);
        }
      });
    }
  }

  function removeAllListeners(): void {
    listeners.clear();
  }

  return {
    on,
    off,
    emit,
    removeAllListeners,
  };
}
